/* tr-core.js — shared web-component base + global enhancers (Te Ratana / d3 Cinematic).
   Liquid-first, progressive enhancement: nothing here is required for first paint. */
(function () {
  var root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');

  var TR = (window.TR = window.TR || {});

  /* Global event bus (cart open/close, refresh, section reloads). */
  TR.bus = new EventTarget();
  TR.emit = function (name, detail) { TR.bus.dispatchEvent(new CustomEvent(name, { detail: detail })); };
  TR.on = function (name, fn, opts) { TR.bus.addEventListener(name, fn, opts); };

  TR.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Base custom element: AbortController-scoped listeners + lifecycle hooks. */
  class TRElement extends HTMLElement {
    connectedCallback() {
      this._ac = new AbortController();
      this.signal = this._ac.signal;
      try { this.init(); } catch (e) { console.error('[TR]', this.tagName, e); }
    }
    disconnectedCallback() { this._ac && this._ac.abort(); this.teardown(); }
    init() {}
    teardown() {}
    on(target, type, handler, opts) {
      target.addEventListener(type, handler, Object.assign({ signal: this.signal }, opts || {}));
    }
    $(sel) { return this.querySelector(sel); }
    $$(sel) { return Array.prototype.slice.call(this.querySelectorAll(sel)); }
  }
  TR.TRElement = TRElement;

  /* Focus-trap helper (used by mobile-nav + cart-drawer). */
  TR.trapFocus = function (container, signal) {
    var sel = 'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var items = Array.prototype.slice.call(container.querySelectorAll(sel)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }, { signal: signal });
  };

  /* ── Cart helpers (server-truth; money always from Liquid) ───────────────── */
  TR.setCartCount = function (n) {
    var c = document.querySelector('.top-nav .cart .count');
    if (c) { c.textContent = '(' + n + ')'; }
    var live = document.querySelector('[data-cart-live]');
    if (live) { live.textContent = n === 1 ? '1 item in your bag' : n + ' items in your bag'; }
  };
  TR.refreshCart = function (sectionHTML) {
    if (!sectionHTML) { return null; }
    var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    var fresh = doc.querySelector('[data-cart-contents]');
    var live = document.querySelector('#cart-drawer [data-cart-contents]');
    if (fresh && live) { live.innerHTML = fresh.innerHTML; }
    // Authoritative count travels inside the re-rendered drawer (add.js omits item_count).
    var countEl = doc.querySelector('[data-cart-count]');
    var n = countEl ? parseInt(countEl.getAttribute('data-cart-count'), 10) : null;
    if (n != null && !isNaN(n)) { TR.setCartCount(n); }
    return n;
  };
  TR.cartChange = function (payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      if (!r.ok) { return r.json().then(function (e) { return Promise.reject(e); }); }
      return r.json();
    });
  };
  TR.cartDrawerId = function () {
    var d = document.getElementById('cart-drawer');
    return (d && d.getAttribute('data-section-id')) || 'cart-drawer';
  };

  /* Theme-editor: re-broadcast Shopify section lifecycle on the bus. */
  document.addEventListener('shopify:section:load', function (e) { TR.emit('section:load', { id: e.detail.sectionId, target: e.target }); });
  document.addEventListener('shopify:section:unload', function (e) { TR.emit('section:unload', { id: e.detail.sectionId, target: e.target }); });

  /* ── Custom elements are defined below (appended as their sections are built) ── */

  class StickyHeader extends TR.TRElement {
    init() {
      var self = this;
      var offset = parseInt(this.dataset.scrollOffset || '60', 10);
      function onScroll() { self.classList.toggle('scrolled', window.scrollY > offset); }
      onScroll();
      this.on(window, 'scroll', onScroll, { passive: true });
      var cartBtn = this.$('[data-cart-open]'); if (cartBtn) this.on(cartBtn, 'click', function () { TR.emit('cart:open'); });
      var navBtn = this.$('[data-mobile-open]'); if (navBtn) this.on(navBtn, 'click', function () { TR.emit('mobilenav:open'); });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  class MobileNav extends TR.TRElement {
    init() {
      var self = this;
      this.dialog = this.$('.mnav');
      this.on(TR.bus, 'mobilenav:open', function () { self.open(); });
      this.$$('[data-mnav-close]').forEach(function (el) { self.on(el, 'click', function () { self.close(); }); });
      this.on(this, 'keydown', function (e) { if (e.key === 'Escape') self.close(); });
      var bag = this.$('[data-cart-open]'); if (bag) this.on(bag, 'click', function () { self.close(); TR.emit('cart:open'); });
      TR.trapFocus(this, this.signal);
    }
    open() {
      this._last = document.activeElement;
      this.setAttribute('open', '');
      this.dialog.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this.dialog.focus();
    }
    close() {
      this.removeAttribute('open');
      this.dialog.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      this._last && this._last.focus();
    }
  }
  customElements.define('mobile-nav', MobileNav);

  class InfiniteMarquee extends TR.TRElement {
    init() {
      var track = this.$('[data-marquee-track]');
      if (!track) return;
      var group = track.firstElementChild;
      if (group) { track.appendChild(group.cloneNode(true)); } // 2× = seamless at translateX(-50%)
    }
  }
  customElements.define('infinite-marquee', InfiniteMarquee);

  class CartDrawer extends TR.TRElement {
    init() {
      var self = this;
      this.dialog = this.$('.cart-d3');
      this.on(TR.bus, 'cart:open', function () { self.open(); });
      this.on(TR.bus, 'cart:close', function () { self.close(); });
      // Event delegation so AJAX content swaps never orphan listeners.
      this.on(this, 'click', function (e) {
        if (e.target.closest('[data-cart-close]')) { self.close(); return; }
        var minus = e.target.closest('[data-cart-minus]');
        var plus = e.target.closest('[data-cart-plus]');
        var remove = e.target.closest('[data-cart-remove]');
        if (minus) { e.preventDefault(); self.step(minus.dataset.line, -1); }
        else if (plus) { e.preventDefault(); self.step(plus.dataset.line, 1); }
        else if (remove) { e.preventDefault(); self.setLine(remove.dataset.line, 0); }
      });
      this.on(this, 'keydown', function (e) { if (e.key === 'Escape') self.close(); });
      TR.trapFocus(this, this.signal);
    }
    step(line, delta) {
      var item = this.querySelector('.item[data-line="' + line + '"] .qval');
      var current = item ? parseInt(item.textContent, 10) || 0 : 0;
      this.setLine(line, Math.max(0, current + delta));
    }
    setLine(line, quantity) {
      var self = this;
      var drawerId = TR.cartDrawerId();
      this.setAttribute('data-busy', '');
      TR.cartChange({ line: parseInt(line, 10), quantity: quantity, sections: drawerId })
        .then(function (d) {
          TR.refreshCart(d.sections && d.sections[drawerId]); // also updates the count
          TR.emit('cart:changed', d);
          var contents = self.querySelector('[data-cart-contents]');
          if (contents) { contents.focus(); } // keep keyboard focus inside the drawer after swap
        })
        .catch(function (err) {
          if (window.console) { console.error('[TR] cart:change failed', err); }
          var live = self.querySelector('[data-cart-live]');
          if (live) { live.textContent = 'Could not update your bag. Please try again.'; }
        })
        .finally(function () { self.removeAttribute('data-busy'); });
    }
    open() {
      this._last = document.activeElement;
      this.setAttribute('open', '');
      this.dialog.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      this.dialog.focus();
    }
    close() {
      this.removeAttribute('open');
      this.dialog.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      this._last && this._last.focus();
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class HeroReel extends TR.TRElement {
    init() {
      var self = this;
      this.slides = this.$$('.reel-slide');
      this.dots = this.$$('.reel-dots .dot');
      this.n = this.slides.length;
      if (this.n < 1) return;
      try { this.meta = JSON.parse(this.$('[data-reel-data]').textContent); } catch (e) { this.meta = []; }
      this.counter = this.$('[data-reel-counter]'); this.teEl = this.$('[data-reel-te]'); this.subEl = this.$('[data-reel-sub]');
      this.i = 0;
      this.ms = parseInt(this.dataset.autoplay || '5200', 10);
      this.dots.forEach(function (d, idx) { self.on(d, 'click', function () { self.go(idx); self.restart(); }); });
      if (this.n > 1 && !TR.reduceMotion.matches) this.start();
      this.on(document, 'visibilitychange', function () { if (document.hidden) { self.stop(); } else { self.restart(); } });
      this.initHeadingDraw();
    }
    /* Animated self-drawing headline stroke. Plain -webkit-text-stroke can't be dashed/animated,
       so we rebuild the English headline as SVG <text> where stroke-dasharray CAN animate.
       No-JS / reduced-motion / outline-off all keep the static CSS outline (we just bail). */
    initHeadingDraw() {
      var self = this;
      var en = this.$('h1 .en');
      if (!en) { return; }
      var outline = this.dataset.headingOutline === 'true';
      var mode = this.dataset.headingAnim || 'shine';
      // none/shine/pulse are CSS-only; only flow/once need the JS SVG rebuild.
      if (!outline || mode === 'none' || mode === 'shine' || mode === 'pulse' || TR.reduceMotion.matches) { return; }
      if (!('SVGSVGElement' in window)) { return; }
      var canvas = document.createElement('canvas');
      if (!canvas.getContext) { return; }
      var build = function () { try { self.buildHeadingSVG(en, mode); } catch (e) { /* keep static outline */ } };
      // Wait for fonts so canvas measurement (line wrapping) matches the rendered headline.
      if (document.fonts && document.fonts.ready && document.fonts.ready.then) { document.fonts.ready.then(build); }
      else { build(); }
      var t;
      this.on(window, 'resize', function () { clearTimeout(t); t = setTimeout(build, 200); }); // wrapping changes with width
    }
    buildHeadingSVG(en, mode) {
      if (en.dataset.enHtml == null) { en.dataset.enHtml = en.innerHTML; } // capture original copy once
      var decoder = document.createElement('span');
      var explicit = en.dataset.enHtml.split(/<br\s*\/?>/i).map(function (s) {
        decoder.innerHTML = s; return (decoder.textContent || '').replace(/\s+/g, ' ').trim();
      }).filter(Boolean);
      if (!explicit.length) { return; }
      var cs = getComputedStyle(en);
      var W = en.clientWidth; // .en is display:block → full content width, independent of current content
      if (!W) { return; }
      var fs = parseFloat(cs.fontSize);
      var lh = parseFloat(cs.lineHeight) || fs * 0.9;
      var ff = cs.fontFamily, fw = cs.fontWeight;
      var ls = cs.letterSpacing === 'normal' ? 0 : (parseFloat(cs.letterSpacing) || 0);
      var ctx = this._mctx || (this._mctx = document.createElement('canvas').getContext('2d'));
      ctx.font = fw + ' ' + fs + 'px ' + ff;
      var meas = function (s) { return ctx.measureText(s).width + (ls ? ls * s.length : 0); };
      var lines = [];
      explicit.forEach(function (line) { // honour authored <br>, then greedy-wrap each to the box width
        var cur = '';
        line.toUpperCase().split(' ').forEach(function (w) {
          var cand = cur ? cur + ' ' + w : w;
          if (meas(cand) > W && cur) { lines.push(cur); cur = w; } else { cur = cand; }
        });
        if (cur) { lines.push(cur); }
      });
      if (!lines.length) { return; }
      var sw = parseFloat(this.dataset.headingStroke) || 1.5;
      var H = Math.round(lines.length * lh);
      var esc = function (s) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); };
      var ffAttr = esc(ff);
      var texts = lines.map(function (ln, i) {
        return '<text x="0" y="' + (fs * 0.78 + i * lh).toFixed(1) + '" stroke-width="' + sw + '"'
          + ' font-family="' + ffAttr + '" font-weight="' + fw + '" font-size="' + fs + 'px"'
          + ' letter-spacing="' + ls + 'px">' + esc(ln) + '</text>';
      }).join('');
      en.innerHTML = '<svg role="img" aria-label="' + esc(explicit.join(' ')) + '"'
        + ' width="' + W + '" height="' + H + '" viewBox="0 0 ' + W + ' ' + H + '">' + texts + '</svg>';
      en.classList.remove('flow', 'once');
      en.classList.add(mode === 'once' ? 'once' : 'flow');
    }
    start() { var self = this; this._t = setInterval(function () { self.go((self.i + 1) % self.n); }, this.ms); }
    stop() { clearInterval(this._t); }
    restart() { this.stop(); if (this.n > 1 && !TR.reduceMotion.matches) this.start(); }
    go(idx) {
      function pad(x) { return String(x).padStart(2, '0'); }
      this.slides[this.i].classList.remove('active'); if (this.dots[this.i]) this.dots[this.i].classList.remove('active');
      this.i = idx;
      this.slides[idx].classList.add('active'); if (this.dots[idx]) this.dots[idx].classList.add('active');
      if (this.counter) this.counter.textContent = pad(idx + 1) + ' / ' + pad(this.n);
      var m = this.meta[idx]; if (m) { if (this.teEl) this.teEl.textContent = m.te || ''; if (this.subEl) this.subEl.textContent = m.sub || ''; }
    }
    teardown() { this.stop(); }
  }
  customElements.define('hero-reel', HeroReel);

  /* Tabs (Hunter/Cooper on Brothers, sections on Village). Buttons [data-tab] toggle [data-panel]. */
  class AthleteTabs extends TR.TRElement {
    init() {
      var self = this;
      this.tabs = this.$$('[data-tab]');
      this.panels = this.$$('[data-panel]');
      if (!this.tabs.length) return;
      this.tabs.forEach(function (t, i) {
        self.on(t, 'click', function () { self.select(i); });
        self.on(t, 'keydown', function (e) {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            var next = (i + (e.key === 'ArrowRight' ? 1 : self.tabs.length - 1)) % self.tabs.length;
            self.select(next); self.tabs[next].focus();
          }
        });
      });
      /* Brothers page: put each athlete's game schedule inside his panel, above the film (owner asked for the
         schedule higher up the page). Skipped in the theme editor so section editing still works in place. */
      if (!(window.Shopify && window.Shopify.designMode)) {
        this.$$('[data-schedule-slot]').forEach(function (slot) {
          var who = slot.getAttribute('data-schedule-slot');
          var sched = document.querySelector('.sched[data-for-athlete="' + who + '"]');
          if (sched && !slot.contains(sched)) { slot.appendChild(sched); sched.classList.add('is-in-panel'); }
        });
      }
      this.$$('[data-goto-tab]').forEach(function (el) {
        self.on(el, 'click', function (e) {
          e.preventDefault();
          self.select(parseInt(el.dataset.gotoTab, 10) || 0);
          window.scrollTo({ top: window.scrollY + self.getBoundingClientRect().top - 70, behavior: 'smooth' });
        });
      });
      this.select(0);
    }
    select(i) {
      this.tabs.forEach(function (t, idx) { t.classList.toggle('active', idx === i); t.setAttribute('aria-selected', idx === i ? 'true' : 'false'); t.tabIndex = idx === i ? 0 : -1; });
      this.panels.forEach(function (p, idx) { p.hidden = idx !== i; });
      /* Brothers page: show only the open athlete's schedule ([data-for-athlete], game-schedule section).
         In the theme editor every schedule stays visible so each one can be edited. */
      var who = this.tabs[i] && this.tabs[i].getAttribute('data-athlete');
      if (!who) return;
      var editing = !!(window.Shopify && window.Shopify.designMode);
      document.querySelectorAll('[data-for-athlete]').forEach(function (el) {
        el.hidden = !editing && el.getAttribute('data-for-athlete') !== who;
      });
      TR.emit('athlete:change', { athlete: who, index: i });
    }
  }
  customElements.define('athlete-tabs', AthleteTabs);

  /* Variant picker — option radios resolve to a variant; updates hidden id, price, button. */
  /* Product gallery: main image follows the selected variant, thumbnails narrow to the selected colour
     (alt text "Title · Colour · view", written by tools/merch_shopify.py), thumbnail click swaps the main image.
     Defined before <variant-picker> so it is listening when the picker announces the initial selection. */
  class PdpGallery extends TR.TRElement {
    init() {
      var self = this;
      this.root = this.closest('[data-pdp]') || document;
      this.main = this.$('[data-pdp-main]');
      this.thumbs = this.$$('[data-media-id]');
      this.thumbs.forEach(function (t) {
        self.on(t, 'click', function () { self.show(t.getAttribute('data-media-id'), t.getAttribute('data-src'), t.getAttribute('data-alt')); });
      });
      TR.on('variant:change', function (e) { if (e.detail && e.detail.root === self.root) self.onVariant(e.detail); }, { signal: this.signal });
    }
    onVariant(d) {
      var colour = d.colour, mine = [];
      if (colour && this.thumbs.length) {
        mine = this.thumbs.filter(function (t) {
          return (t.getAttribute('data-alt') || '').split('·').map(function (s) { return s.trim(); }).indexOf(colour) !== -1;
        });
        var visible = (mine.length ? mine : this.thumbs).slice(0, 6);
        this.thumbs.forEach(function (t) { t.hidden = visible.indexOf(t) === -1; });
      }
      var fm = d.variant && d.variant.featured_media;
      if (fm && fm.preview_image && fm.preview_image.src) this.show(String(fm.id), fm.preview_image.src, fm.alt);
      else if (mine[0]) this.show(mine[0].getAttribute('data-media-id'), mine[0].getAttribute('data-src'), mine[0].getAttribute('data-alt'));
    }
    show(id, src, alt) {
      if (!this.main || !src) return;
      var url = /[?&]width=/.test(src) ? src : src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=1100';
      if (this.main.getAttribute('src') !== url) this.main.setAttribute('src', url);
      if (alt != null) this.main.setAttribute('alt', alt);
      this.thumbs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-media-id') === String(id)); });
    }
  }
  customElements.define('pdp-gallery', PdpGallery);

  class VariantPicker extends TR.TRElement {
    init() {
      var self = this;
      this.root = this.closest('[data-pdp]') || document;
      try { this.variants = JSON.parse(this.$('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.idInput = this.root.querySelector('[data-variant-id]');
      this.priceEl = this.root.querySelector('[data-price]');
      this.addBtn = this.root.querySelector('[data-add-btn]');
      this.on(this, 'change', function (e) { if (e.target.matches('input[type="radio"]')) self.update(); });
      this.announce(this.match()); // let the gallery narrow its thumbnails to the initial colour
    }
    match() {
      var sel = this.selected();
      return this.variants.find(function (v) { return v.options.every(function (opt, i) { return opt === sel[i]; }); });
    }
    colour() {
      var fs = this.$$('fieldset[data-option-name]').find(function (f) { return /colou?r/.test(f.getAttribute('data-option-name')); });
      var checked = fs && fs.querySelector('input:checked');
      return checked ? checked.value : null;
    }
    announce(variant) {
      TR.emit('variant:change', { root: this.root, variant: variant || null, colour: this.colour() });
    }
    selected() {
      return this.$$('fieldset[data-option-position]').sort(function (a, b) {
        return parseInt(a.dataset.optionPosition, 10) - parseInt(b.dataset.optionPosition, 10);
      }).map(function (fs) {
        var checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }
    update() {
      var sel = this.selected();
      this.$$('.opt').forEach(function (l) { var inp = l.querySelector('input'); if (inp) { l.classList.toggle('active', inp.checked); } });
      var match = this.variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === sel[i]; });
      });
      if (this.idInput && match) { this.idInput.value = match.id; }
      this.setAvailability(match);
      if (match) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState(window.history.state || {}, '', url);
      }
      this.announce(match);
    }
    setAvailability(match) {
      if (!this.addBtn) { return; }
      var txt = this.addBtn.querySelector('[data-add-text]');
      var icon = this.addBtn.querySelector('[data-add-icon]');
      if (!match) {
        this.addBtn.disabled = true; if (txt) txt.textContent = 'Unavailable'; if (icon) icon.textContent = '';
      } else if (!match.available) {
        this.addBtn.disabled = true; if (txt) txt.textContent = 'Sold out'; if (icon) icon.textContent = '';
      } else {
        this.addBtn.disabled = false; if (txt) txt.textContent = 'Add to bag'; if (icon) icon.textContent = '+';
      }
      if (this.priceEl && match && match.price != null) {
        this.priceEl.textContent = TR.formatMoney(match.price);
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Money formatter — uses the shop format injected on window; falls back to a plain string.
     Liquid remains source of truth for displayed totals; this only updates the PDP price label. */
  TR.formatMoney = function (cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || window.Shopify.money_with_currency_format || '');
    }
    return '$' + (cents / 100).toFixed(2);
  };

  /* Product form — AJAX add to cart with Bundled Section Rendering, opens the drawer. */
  class ProductForm extends TR.TRElement {
    init() {
      this.form = this.$('form');
      if (!this.form) { return; }
      this.btn = this.$('[data-add-btn]');
      this.errEl = this.$('[data-form-error]');
      this.on(this.form, 'submit', this.onSubmit.bind(this));
    }
    onSubmit(e) {
      e.preventDefault();
      var self = this;
      this.clearError();
      this.setBusy(true);
      var body = new FormData(this.form);
      var drawerId = TR.cartDrawerId();
      body.append('sections', drawerId);
      fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: body })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
        .then(function (res) {
          if (!res.ok) { self.showError((res.d && res.d.description) || 'Could not add to bag.'); return; }
          TR.refreshCart(res.d.sections && res.d.sections[drawerId]); // updates contents + count
          TR.emit('cart:open');
          TR.emit('cart:changed', res.d);
        })
        .catch(function () { self.showError('Network error — please try again.'); })
        .finally(function () { self.setBusy(false); });
    }
    setBusy(on) {
      if (!this.btn) { return; }
      this.btn.setAttribute('aria-busy', on ? 'true' : 'false');
      this.btn.disabled = on;
    }
    showError(msg) { if (this.errEl) { this.errEl.textContent = msg; this.errEl.hidden = false; } }
    clearError() { if (this.errEl) { this.errEl.textContent = ''; this.errEl.hidden = true; } }
  }
  customElements.define('product-form', ProductForm);

  /* Facet filters — progressive enhancement of the collection filter/sort form.
     Submits via the Section Rendering API and swaps only the grid; pushes a clean URL. */
  class FacetFilters extends TR.TRElement {
    init() {
      this.form = this.$('form');
      this.sectionId = this.dataset.sectionId;
      if (!this.form) { return; }
      var self = this;
      var apply = this.$('[data-facet-apply]');
      if (apply) { apply.style.display = 'none'; } // JS auto-applies; button kept for no-JS
      this.on(this.form, 'submit', function (e) { e.preventDefault(); self.apply(); });
      this.on(this.form, 'change', function (e) {
        if (e.target.type === 'number') { return; } // price inputs are debounced below
        self.apply();
      });
      this.on(this.form, 'input', function (e) {
        if (e.target.type !== 'number') { return; }
        clearTimeout(self._t);
        self._t = setTimeout(function () { self.apply(); }, 500);
      });
      // Back/forward: reload to the URL's filter state (avoids re-applying a stale form).
      this.on(window, 'popstate', function () { window.location.reload(); });
    }
    apply() {
      var self = this;
      var params = new URLSearchParams();
      new FormData(this.form).forEach(function (v, k) { if (v !== '') { params.append(k, v); } });
      var qs = params.toString();
      var clean = window.location.pathname + (qs ? '?' + qs : '');
      var fetchUrl = window.location.pathname + '?section_id=' + this.sectionId + (qs ? '&' + qs : '');
      this.setAttribute('data-busy', '');
      var grid = document.querySelector('[data-collection-grid]');
      if (grid) { grid.setAttribute('data-busy', ''); }
      fetch(fetchUrl)
        .then(function (r) { if (!r.ok) { throw new Error('section render ' + r.status); } return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshGrid = doc.querySelector('[data-collection-grid]');
          var liveGrid = document.querySelector('[data-collection-grid]');
          if (freshGrid && liveGrid) { liveGrid.innerHTML = freshGrid.innerHTML; }
          var freshRow = doc.querySelector('.facet-row');
          var liveRow = self.querySelector('.facet-row');
          if (freshRow && liveRow) { liveRow.innerHTML = freshRow.innerHTML; }
          window.history.pushState({ facets: true }, '', clean);
        })
        .catch(function () {})
        .finally(function () {
          self.removeAttribute('data-busy');
          var g = document.querySelector('[data-collection-grid]');
          if (g) { g.removeAttribute('data-busy'); }
        });
    }
    teardown() { clearTimeout(this._t); }
  }
  customElements.define('facet-filters', FacetFilters);

  /* Cart page — AJAX qty/remove on the full /cart page; swaps its own section + the drawer. */
  class CartPage extends TR.TRElement {
    init() {
      var self = this;
      this.sectionId = this.dataset.sectionId;
      this.on(this, 'click', function (e) {
        var minus = e.target.closest('[data-cart-minus]');
        var plus = e.target.closest('[data-cart-plus]');
        var remove = e.target.closest('[data-cart-remove]');
        if (minus) { e.preventDefault(); self.step(minus.dataset.line, -1); }
        else if (plus) { e.preventDefault(); self.step(plus.dataset.line, 1); }
        else if (remove) { e.preventDefault(); self.setLine(remove.dataset.line, 0); }
      });
      this.on(this, 'change', function (e) {
        var input = e.target.closest('[data-qty-input]');
        if (!input) { return; }
        var lineEl = input.closest('[data-line]');
        if (lineEl) { self.setLine(lineEl.dataset.line, Math.max(0, parseInt(input.value, 10) || 0)); }
      });
    }
    step(line, delta) {
      var input = this.querySelector('[data-line="' + line + '"] [data-qty-input]');
      var current = input ? parseInt(input.value, 10) || 0 : 0;
      this.setLine(line, Math.max(0, current + delta));
    }
    setLine(line, quantity) {
      var self = this;
      var drawerId = TR.cartDrawerId();
      this.setAttribute('data-busy', '');
      TR.cartChange({ line: parseInt(line, 10), quantity: quantity, sections: drawerId + ',' + this.sectionId })
        .then(function (d) {
          var fresh = d.sections && d.sections[self.sectionId];
          if (fresh) {
            var doc = new DOMParser().parseFromString(fresh, 'text/html');
            var freshContents = doc.querySelector('[data-cart-page-contents]');
            var liveContents = self.querySelector('[data-cart-page-contents]');
            if (freshContents && liveContents) { liveContents.innerHTML = freshContents.innerHTML; liveContents.focus(); }
          }
          TR.refreshCart(d.sections && d.sections[drawerId]); // also updates the count
        })
        .catch(function (err) {
          if (window.console) { console.error('[TR] cart:change failed', err); }
          var live = document.querySelector('[data-cart-live]');
          if (live) { live.textContent = 'Could not update your bag. Please try again.'; }
        })
        .finally(function () { self.removeAttribute('data-busy'); });
    }
  }
  customElements.define('cart-page', CartPage);

  /* ── Motion system: scroll-reveal + stagger + reveal-text + count-up + parallax ──
     One shared IntersectionObserver for reveals/countups; a passive rAF loop for parallax.
     Reduced-motion + no-JS safe (base.css shows content; JS bails to final state). */
  TR.motion = (function () {
    var io = null;
    function reduced() { return TR.reduceMotion.matches; }
    function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

    function countUp(el) {
      var raw = el.getAttribute('data-countup-raw');
      if (raw === null) { raw = el.textContent; el.setAttribute('data-countup-raw', raw); }
      var m = raw.match(/^(\D*)(\d[\d,]*(?:\.\d+)?)(.*)$/);
      if (!m || reduced()) { return; }
      var pre = m[1], numStr = m[2], suf = m[3];
      var hasComma = numStr.indexOf(',') !== -1;
      var clean = numStr.replace(/,/g, '');
      var parts = clean.split('.');
      var decimals = parts[1] ? parts[1].length : 0;
      var intLen = parts[0].length;
      var target = parseFloat(clean);
      var dur = 1200, startTs = null;
      function fmt(v) {
        var s = v.toFixed(decimals);
        var sp = s.split('.');
        var ip = sp[0];
        while (ip.length < intLen) { ip = '0' + ip; }
        if (hasComma) { ip = ip.replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
        return pre + (decimals ? ip + '.' + sp[1] : ip) + suf;
      }
      function tick(ts) {
        if (startTs === null) { startTs = ts; }
        var p = Math.min(1, (ts - startTs) / dur);
        el.textContent = fmt(target * easeOutCubic(p));
        if (p < 1) { requestAnimationFrame(tick); } else { el.textContent = raw; }
      }
      requestAnimationFrame(tick);
    }

    function splitText(el) {
      if (el.dataset.rtDone) { return; }
      var lines = el.innerHTML.split(/<br\s*\/?>/i);
      el.innerHTML = lines.map(function (ln) {
        return '<span class="rt-line"><span class="rt-inner">' + ln + '</span></span>';
      }).join('');
      el.dataset.rtDone = '1';
    }

    function reveal(el) {
      if (el.hasAttribute('data-reveal-stagger')) {
        Array.prototype.slice.call(el.children).forEach(function (kid, i) {
          kid.style.setProperty('--reveal-delay', (reduced() ? 0 : i * 90) + 'ms');
          kid.classList.add('is-visible');
        });
      } else if (el.hasAttribute('data-reveal-text')) {
        Array.prototype.forEach.call(el.querySelectorAll('.rt-inner'), function (n, i) {
          n.style.setProperty('--reveal-delay', (reduced() ? 0 : i * 120) + 'ms');
        });
        el.classList.add('is-visible');
      } else {
        el.classList.add('is-visible');
      }
      if (el.hasAttribute('data-countup')) { countUp(el); }
    }

    function scan(root) {
      var nodes = (root || document).querySelectorAll('[data-reveal],[data-reveal-stagger],[data-reveal-text],[data-countup]');
      Array.prototype.forEach.call(nodes, function (n) {
        if (n.dataset.motionReady) { return; }
        n.dataset.motionReady = '1';
        if (n.hasAttribute('data-reveal-text') && !reduced()) { splitText(n); }
        if (reduced() || !io) { reveal(n); return; }
        io.observe(n);
      });
    }

    function initParallax() {
      if (reduced() || window.innerWidth < 1000) { return; }
      var els = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
      if (!els.length) { return; }
      var ticking = false;
      function update() {
        if (reduced() || window.innerWidth < 1000) {
          els.forEach(function (el) { el.style.removeProperty('--parallax-y'); });
          ticking = false; return;
        }
        var vh = window.innerHeight;
        els.forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) { return; }
          var mag = parseFloat(el.getAttribute('data-parallax')) || 24;
          var progress = (r.top + r.height / 2 - vh / 2) / vh;
          el.style.setProperty('--parallax-y', (progress * mag).toFixed(1) + 'px');
        });
        ticking = false;
      }
      function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    function init() {
      if (('IntersectionObserver' in window) && !reduced()) {
        /* Reveal at 12% visible — or, for elements taller than the screen can ever show 12% of (a long
           article body on a phone), once they fill a fifth of the viewport. A plain 0.12 threshold left
           such elements stuck at opacity 0. Fine-grained thresholds keep the callback firing while scrolling. */
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            if (e.intersectionRatio >= 0.12 || e.intersectionRect.height >= window.innerHeight * 0.2) {
              io.unobserve(e.target); reveal(e.target);
            }
          });
        }, { threshold: [0, 0.01, 0.02, 0.04, 0.06, 0.08, 0.1, 0.12], rootMargin: '0px 0px -8% 0px' });
      }
      scan(document);
      initParallax();
      TR.on('section:load', function (e) { scan((e.detail && e.detail.target) || document); });
    }

    return { init: init, scan: scan };
  })();
  TR.motion.init();
})();
